/**
 * The file API, typed at the edge of this package.
 *
 * The upload goes through `ApiClient.upload`, which was added to `core-api`
 * rather than written here. Multipart is a transport concern, not a
 * file-library one, and a feature reaching for `fetch` directly would quietly
 * opt out of the refresh-and-retry, the session-lost notification and the
 * error mapping that every other call gets.
 */

import type { ApiClient } from "@hardauto/core-api";

export interface StoredFile {
  id: string;
  filename: string;
  content_type: string;
  size: number;
  checksum: string;
  owner_user_id: string | null;
  created_at: string | null;
  download_url: string | null;
}

export interface FileList {
  files: StoredFile[];
  next_cursor: string | null;
}

const BASE = "/api/v1/storage/files";

export class FilesApi {
  readonly #client: ApiClient;

  constructor(client: ApiClient) {
    this.#client = client;
  }

  list(
    params: { after_id?: string | undefined; limit?: number | undefined } = {},
  ): Promise<FileList> {
    const search = new URLSearchParams();
    if (params.after_id) search.set("after_id", params.after_id);
    if (params.limit) search.set("limit", String(params.limit));
    const query = search.toString();
    return this.#client.request<FileList>(query ? `${BASE}?${query}` : BASE);
  }

  upload(file: File): Promise<StoredFile> {
    const body = new FormData();
    body.append("file", file);
    return this.#client.upload<StoredFile>(BASE, body);
  }

  remove(id: string): Promise<void> {
    return this.#client.request<void>(`${BASE}/${id}`, { method: "DELETE" });
  }

  /**
   * The download path.
   *
   * Returned as a path rather than fetched: the response carries
   * `Content-Disposition: attachment`, so letting the browser navigate to it
   * is what makes the file save. Fetching it into memory first would work and
   * would also hold a large file in the tab for no reason.
   */
  downloadPath(id: string): string {
    return `${BASE}/${id}/content`;
  }
}
